
import React, { useState, useMemo } from 'react';
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { QuickCompanyAdd } from "@/components/companies/QuickCompanyAdd";
import { Company } from "@/types/company";
import { useUpdateCompany } from "@/hooks/useCompanies";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface CompanySelectorProps {
  companyId: string;
  onCompanyChange: (companyId: string) => void;
  onCompanyAdded: (companyId: string, companyName: string) => void;
  companies: Company[];
}

export const CompanySelector = ({ companyId, onCompanyChange, onCompanyAdded, companies }: CompanySelectorProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const updateCompanyMutation = useUpdateCompany();

  const selectedCompanyDetails = useMemo(() => {
    if (!companyId) return null;
    return companies.find(c => c.id === companyId);
  }, [companyId, companies]);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCompanyDetails) return;

    setIsUploading(true);
    try {
        const filePath = `public/${selectedCompanyDetails.id}/${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
            .from('company-logos')
            .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
            .from('company-logos')
            .getPublicUrl(filePath);

        await updateCompanyMutation.mutateAsync({
            id: selectedCompanyDetails.id,
            logo_url: publicUrl,
        });

        toast.success("تم تحديث شعار الشركة بنجاح.");
    } catch (error) {
        console.error("Error uploading logo:", error);
        toast.error("فشل في تحميل الشعار.");
    } finally {
        setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="company">الشركة</Label>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Select value={companyId} onValueChange={onCompanyChange}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="اختر الشركة (اختياري)" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name_ar}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <QuickCompanyAdd onCompanyAdded={onCompanyAdded} />
          </div>
        </div>
      </div>

      {/* رسالة توضيحية */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex items-start gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
          <div className="text-sm text-blue-700">
            <strong>ملاحظة:</strong> في حال عدم اختيار شركة، سيتم ربط العميل تلقائياً بشركة "ترافليون".
          </div>
        </div>
      </div>

      {selectedCompanyDetails && (
        <div className="mt-2 p-3 border rounded-md flex items-center gap-4 bg-gray-50">
          {selectedCompanyDetails.logo_url ? (
            <img src={selectedCompanyDetails.logo_url} alt="شعار الشركة" className="w-16 h-16 object-contain rounded-md bg-white border" />
          ) : (
            <div className="w-16 h-16 bg-gray-200 rounded-md flex items-center justify-center text-gray-500 text-xs text-center p-1">لا يوجد شعار</div>
          )}
          <div className="space-y-2 flex-1">
            <Label htmlFor="logo-upload" className="text-sm font-normal cursor-pointer block">
              {selectedCompanyDetails.logo_url ? 'تغيير الشعار' : 'رفع شعار للشركة'}
            </Label>
            <Input id="logo-upload" type="file" onChange={handleLogoUpload} disabled={isUploading} className="text-xs h-9" accept="image/*" />
            {isUploading && <div className="flex items-center gap-2 text-xs text-blue-600 mt-1"><Loader2 className="w-3 h-3 animate-spin" /> <span>جاري الرفع...</span></div>}
          </div>
        </div>
      )}
    </div>
  );
};

